// src/features/chat/components/RecordDraftMessage.jsx

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FilePen,
  Check,
  X,
  Calendar,
  MapPin,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import PropTypes from 'prop-types';
import useRecordDraft from '../../records/hooks/useRecordDraft';
import { usePromoteRecordDraft } from '../../records/hooks/useRecordMutations';
import CompactLinkedRecords from '../../records/components/shared/CompactLinkedRecords';
import { formatDateRange } from '../../records/utils/dateRangeDisplay';
import styles from './RecordDraftMessage.module.css';

/**
 * Display AI-created record drafts in chat.
 * Supports expand/collapse and "Save as Record" action.
 */
const RecordDraftMessage = ({ draftId }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { draft, loading, error } = useRecordDraft(draftId);
  const promoteDraft = usePromoteRecordDraft();
  const isSaving = promoteDraft.isPending;

  const handleSave = useCallback(
    async (e) => {
      e.stopPropagation();
      if (isSaving || saveSuccess) return;

      if (!draftId) {
        setSaveError(t('assistant.draft_invalid', 'Invalid draft'));
        setIsExpanded(true);
        return;
      }

      setSaveError(null);

      try {
        const result = await promoteDraft.mutateAsync({ draftId });

        if (result?.success && result?.recordId) {
          setSaveSuccess(true);
          setTimeout(() => {
            navigate(`/record/${result.recordId}`);
          }, 400);
        }
      } catch (err) {
        console.error('Error saving draft as record:', err);
        setSaveError(err.message || t('assistant.save_error', 'Failed to save'));
        setIsExpanded(true);
      }
    },
    [draftId, isSaving, navigate, promoteDraft, saveSuccess, t]
  );

  const handleCloseExpanded = useCallback((e) => {
    e.stopPropagation();
    setIsExpanded(false);
  }, []);

  if (loading) {
    return (
      <div className={styles.draft} data-state="loading" aria-busy="true">
        <div className={styles.skeleton}>
          <div className={styles.skeletonIcon} />
          <div className={styles.skeletonTitle} />
        </div>
      </div>
    );
  }

  if (error) {
    const code = error.code;
    const message =
      code === 'not-found'
        ? t('assistant.draft_not_found', 'Draft not found')
        : code === 'permission-denied' || code === 'unauthenticated'
        ? t('errors.unauthenticated')
        : t('assistant.draft_fetch_error', 'Failed to load draft');

    return (
      <div className={styles.draft} data-state="error" role="alert">
        <AlertCircle size={16} className={styles.errorIcon} />
        <span className={styles.errorText}>{message}</span>
      </div>
    );
  }

  if (!draft) {
    return null;
  }

  const rawTitle = draft.content?.title?.trim();
  const title = rawTitle || t('assistant.untitled_draft', 'Untitled');
  const body = draft.content?.body || '';
  const hasBody = Boolean(body.trim());
  const tags = Array.isArray(draft.content?.tags) ? draft.content.tags : [];
  const links = Array.isArray(draft.links?.outgoing) ? draft.links.outgoing : [];
  const hasLinks = links.length > 0;

  const dateDisplay = formatDateRange(draft.attributes?.date, { locale });

  const locationName = draft.attributes?.location?.name || null;
  const isTask = draft.attributes?.isComplete !== undefined;
  const hasAttributes = Boolean(dateDisplay || locationName || isTask);
  const hasExpandedContent = Boolean(
    hasBody || hasAttributes || tags.length > 0 || hasLinks || saveError
  );
  const contentId = `draft-${draftId}`;

  const handleToggleExpand = () => {
    if (!hasExpandedContent) return;
    setIsExpanded((prev) => !prev);
  };

  return (
    <div
      className={styles.draft}
      data-state={isExpanded ? 'expanded' : 'compact'}
      data-saving={isSaving ? 'true' : undefined}
      data-success={saveSuccess ? 'true' : undefined}
      data-expandable={hasExpandedContent ? 'true' : undefined}
    >
      <div className={styles.header}>
        <button
          type="button"
          className={styles.toggleButton}
          onClick={handleToggleExpand}
          aria-expanded={hasExpandedContent ? isExpanded : undefined}
          aria-controls={hasExpandedContent ? contentId : undefined}
          disabled={!hasExpandedContent}
        >
          <span className={styles.icon}>
            <FilePen size={18} />
          </span>

          <span className={styles.title}>{title}</span>
        </button>

        <div className={styles.actions}>
          {isExpanded ? (
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleCloseExpanded}
              aria-label={t('common.close', 'Close')}
            >
              <X size={18} />
            </button>
          ) : (
            <button
              type="button"
              className={styles.saveButton}
              onClick={handleSave}
              disabled={isSaving || saveSuccess}
              aria-label={t('assistant.save_as_record', 'Save as Record')}
            >
              {isSaving ? (
                <Loader2 size={16} className={styles.spinner} />
              ) : saveSuccess ? (
                <Check size={16} />
              ) : (
                t('common.save', 'Save')
              )}
            </button>
          )}
        </div>
      </div>

      {isExpanded && hasExpandedContent && (
        <div className={styles.content} id={contentId}>
          {hasBody && (
            <div className={styles.body}>
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {body}
              </ReactMarkdown>
            </div>
          )}

          {hasAttributes && (
            <div className={styles.attributes}>
              {dateDisplay && (
                <span className={styles.attribute}>
                  <Calendar size={14} />
                  {dateDisplay}
                </span>
              )}
              {locationName && (
                <span className={styles.attribute}>
                  <MapPin size={14} />
                  {locationName}
                </span>
              )}
              {isTask && (
                <span className={styles.attribute}>
                  <CheckCircle size={14} />
                  {t('assistant.task', 'Task')}
                </span>
              )}
            </div>
          )}

          {tags.length > 0 && (
            <div className={styles.tags}>
              {tags.map((tag, index) => {
                const tagLabel = String(tag);
                const displayTag = tagLabel.startsWith('#') ? tagLabel : `#${tagLabel}`;

                return (
                  <span key={index} className={styles.tag}>
                    {displayTag}
                  </span>
                );
              })}
            </div>
          )}

          {hasLinks && (
            <CompactLinkedRecords links={links} maxItems={links.length} />
          )}

          <button
            type="button"
            className={styles.saveButtonExpanded}
            onClick={handleSave}
            disabled={isSaving || saveSuccess}
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className={styles.spinner} />
                {t('assistant.saving', 'Saving...')}
              </>
            ) : saveSuccess ? (
              <>
                <Check size={16} />
                {t('assistant.saved', 'Saved!')}
              </>
            ) : (
              <>
                {t('assistant.save_as_record', 'Save as Record')}
              </>
            )}
          </button>

          {saveError && (
            <div className={styles.saveError} role="alert">
              <AlertCircle size={14} />
              <span>{saveError}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

RecordDraftMessage.propTypes = {
  draftId: PropTypes.string.isRequired,
};

export default RecordDraftMessage;
